// SocialSetup — first-run social connection wizard.
//
// Reads brandId from the URL search params (?brandId=xxx).
// All API calls pass brandId so connections are stored per-brand.
//
// returnPath logic:
//   - If brandId is present AND the user came from brand creation, navigate
//     to that brand's campaign list when done.
//   - Otherwise (initial onboarding), navigate to the dashboard.

import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { api, SocialConnection, SocialProvider } from "../../lib/api";
import { SocialIcon } from "../../components/SocialIcon";

interface Props {
  onComplete: () => void;
}

const PROVIDER_META: Record<
  SocialProvider,
  { label: string; description: string }
> = {
  meta_instagram: {
    label: "Instagram",
    description:
      "Publish posts & stories via the Instagram Content Publishing API",
  },
  meta_facebook: {
    label: "Facebook",
    description: "Post to your Facebook Pages via the Graph API",
  },
  linkedin: {
    label: "LinkedIn",
    description: "Share articles & updates on LinkedIn",
  },
  x: {
    label: "X (Twitter)",
    description: "Post tweets via the X API",
  },
  bluesky: {
    label: "Bluesky",
    description: "Post to the Bluesky network via the AT Protocol",
  },
};

const ALL_PROVIDERS: SocialProvider[] = [
  "meta_instagram",
  "meta_facebook",
  "linkedin",
  "x",
];

export default function SocialSetup({ onComplete }: Props) {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  // brandId scopes all connections. Empty string = legacy / not yet set.
  const brandId = searchParams.get("brandId") ?? "";
  // fromBrandCreate=1 signals we arrived here right after creating a new brand.
  const fromBrandCreate = searchParams.get("from") === "brand-create";

  const [connections, setConnections] = useState<SocialConnection[]>([]);
  const [loading, setLoading] = useState(true);
  const [skipping, setSkipping] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const connectedParam = searchParams.get("connected") as SocialProvider | null;
  const errorParam = searchParams.get("error");

  useEffect(() => {
    api.social
      .list(brandId)
      .then(({ connections: c }) => setConnections(c))
      .catch(() => setError("Failed to load connections"))
      .finally(() => setLoading(false));
  }, [brandId]);

  // Refresh after a successful OAuth redirect
  useEffect(() => {
    if (connectedParam) {
      api.social
        .list(brandId)
        .then(({ connections: c }) => setConnections(c))
        .catch(console.error);
    }
  }, [connectedParam, brandId]);

  const handleConnect = async (provider: SocialProvider) => {
    setError(null);
    try {
      // returnPath tells the backend where to redirect after OAuth —
      // we want to come back to this page with the brandId preserved.
      const returnPath = `/setup/social`;
      const { authUrl } = await api.social.startOAuth(
        provider,
        brandId,
        returnPath,
      );
      window.location.href = authUrl;
    } catch {
      setError(
        `Could not start ${PROVIDER_META[provider].label} connection. Check that the integration is configured.`,
      );
    }
  };

  const handleDisconnect = async (provider: SocialProvider) => {
    try {
      await api.social.disconnect(provider, brandId);
      setConnections((prev) => prev.filter((c) => c.provider !== provider));
    } catch {
      setError(`Failed to disconnect ${PROVIDER_META[provider].label}.`);
    }
  };

  // Determine where to send the user after finishing/skipping setup.
  // • fromBrandCreate: came here after adding a *second* brand → brand's campaigns
  // • Initial onboarding: go to Review Queue (content was just generated)
  const getPostSetupPath = () => {
    if (fromBrandCreate && brandId) {
      return `/brands/${brandId}/campaigns`;
    }
    return "/review";
  };

  const handleFinish = async () => {
    try {
      await api.me.patchProfile({ socialConnectOnboardingCompleted: true });
      onComplete();
      navigate(getPostSetupPath(), { replace: true });
    } catch {
      setError("Failed to save. Please try again.");
    }
  };

  const handleSkip = async () => {
    setSkipping(true);
    try {
      await api.me.patchProfile({
        socialConnectOnboardingCompleted: true,
        socialConnectSkippedAt: Date.now(),
      });
      onComplete();
      navigate(getPostSetupPath(), { replace: true });
    } catch {
      setError("Failed to save. Please try again.");
    } finally {
      setSkipping(false);
    }
  };

  const connectedCount = connections.length;

  return (
    <div className="max-w-2xl mx-auto">
      {/* Header */}
      <div className="text-center mb-10">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl bg-brand-50 mb-4">
          <span className="text-2xl">📡</span>
        </div>
        <h1 className="page-title mb-2">Connect your social accounts</h1>
        <p className="text-muted text-sm max-w-md mx-auto">
          {fromBrandCreate
            ? "Connect the social accounts for this brand. Each brand has its own independent connections."
            : "Postza can publish approved posts directly to your platforms. You can connect accounts now or skip and do it later in Settings."}
        </p>
      </div>

      {/* OAuth callback feedback */}
      {connectedParam && PROVIDER_META[connectedParam] && (
        <div className="mb-6 rounded-lg bg-green-50 border border-green-200 px-4 py-3 text-sm text-green-700">
          {PROVIDER_META[connectedParam].label} connected successfully.
        </div>
      )}
      {errorParam && (
        <div className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {errorParam === "oauth_failed"
            ? `Connection failed for ${searchParams.get("provider") ?? "the selected platform"}. Please try again.`
            : errorParam === "encryption_not_configured"
              ? "Publisher token storage is not yet configured. Contact your administrator."
              : "Something went wrong. Please try again."}
        </div>
      )}
      {error && (
        <div className="mb-6 rounded-lg bg-red-50 border border-red-200 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {/* Platform cards */}
      {loading ? (
        <div className="flex items-center justify-center py-12">
          <div className="w-6 h-6 border-4 border-brand-500 border-t-transparent rounded-full animate-spin" />
        </div>
      ) : (
        <div className="space-y-3 mb-8">
          {ALL_PROVIDERS.map((provider) => {
            const meta = PROVIDER_META[provider];
            const conn = connections.find((c) => c.provider === provider);
            return (
              <div
                key={provider}
                className="card p-5 flex items-center justify-between gap-4"
              >
                <div className="flex items-center gap-4">
                  <SocialIcon provider={provider} size={44} />
                  <div>
                    <p className="text-sm font-semibold text-ink">
                      {meta.label}
                      {conn && (
                        <span className="ml-2 inline-flex items-center gap-1 text-xs font-normal text-green-600">
                          <span className="w-1.5 h-1.5 rounded-full bg-green-500 inline-block" />
                          {conn.displayName ? conn.displayName : "Connected"}
                        </span>
                      )}
                    </p>
                    <p className="text-xs text-muted mt-0.5">
                      {meta.description}
                    </p>
                  </div>
                </div>
                <div className="flex-shrink-0">
                  {conn ? (
                    <button
                      onClick={() => handleDisconnect(provider)}
                      className="text-xs text-red-600 hover:text-red-700 font-medium px-3 py-1.5 border border-red-200 rounded-lg hover:bg-red-50 transition-colors"
                    >
                      Disconnect
                    </button>
                  ) : (
                    <button
                      onClick={() => handleConnect(provider)}
                      className="text-xs font-semibold px-4 py-1.5 rounded-lg bg-brand-600 text-white hover:bg-brand-700 transition-colors"
                    >
                      Connect
                    </button>
                  )}
                </div>
              </div>
            );
          })}
        </div>
      )}

      {/* Actions */}
      <div className="flex items-center justify-between pt-4 border-t border-gray-200">
        <button
          onClick={handleSkip}
          disabled={skipping}
          className="text-sm text-muted hover:text-slate font-medium"
        >
          {skipping ? "Saving…" : "Skip for now"}
        </button>

        <div className="flex items-center gap-3">
          {connectedCount > 0 && (
            <span className="text-sm text-muted">
              {connectedCount} of {ALL_PROVIDERS.length} connected
            </span>
          )}
          <button onClick={handleFinish} className="btn-primary">
            {connectedCount > 0
              ? "Finish setup"
              : "Continue without connecting"}
          </button>
        </div>
      </div>

      {connectedCount === 0 && (
        <p className="text-xs text-faint text-center mt-4">
          Posts will remain manual until you connect at least one account. You
          can connect platforms anytime from{" "}
          <button
            onClick={() => navigate("/settings/platforms")}
            className="underline hover:text-slate"
          >
            Settings → Connected Platforms
          </button>
          .
        </p>
      )}
    </div>
  );
}
